import { getIndustryValue, HOME_SERVICES_TYPE } from './businessCategories';

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const DEFAULT_BEST_TIMES = {
  Monday: '07',
  Tuesday: '07',
  Wednesday: '07',
  Thursday: '07',
  Friday: '07',
  Saturday: '07',
  Sunday: '07'
};

const TIME_ZONE_VALUES = {
  Eastern: 'Eastern Daylight Time - (GMT-4)',
  Central: 'Central Daylight Time - (GMT-5)',
  Mountain: 'Mountain Daylight Time - (GMT-6)',
  'Mountain – Arizona (no daylight saving)': 'Mountain Standard Time - (GMT-7)',
  Pacific: 'Pacific Daylight Time - (GMT-7)',
  Alaska: 'Alaska Daylight Time - (GMT-8)',
  Hawaii: 'Hawaii-Aleutian Standard Time - (GMT-10)'
};

const PLATFORM_FLAGS = {
  Facebook: 'NO_FB',
  Instagram: 'NO_IG',
  Google: 'NO_GMB',
  TikTok: 'NO_TIKTOK'
};
const DEFAULT_PLATFORMS = Object.keys(PLATFORM_FLAGS);
const DEFAULT_SELECTED_PLATFORMS = ['Google'];

export const getPlatformSelections = (clientFlags = []) => {
  const hasSavedPlatformPreferences = DEFAULT_PLATFORMS.some((platform) =>
    clientFlags.includes(PLATFORM_FLAGS[platform])
  );
  if (!hasSavedPlatformPreferences) return DEFAULT_SELECTED_PLATFORMS;

  return DEFAULT_PLATFORMS.filter(
    (platform) => !clientFlags.includes(PLATFORM_FLAGS[platform])
  );
};

export const buildClientFlagChanges = (
  currentFlags = [],
  desiredFlags = [],
  managedFlags = []
) => {
  const current = new Set(currentFlags);
  const desired = new Set(desiredFlags);
  const add = managedFlags.filter(
    (flag) => desired.has(flag) && !current.has(flag)
  );
  const remove = managedFlags.filter(
    (flag) => current.has(flag) && !desired.has(flag)
  );
  return add.length || remove.length ? { add, remove } : null;
};

export const applyClientFlagChanges = (clientFlags = [], changes = {}) => {
  const flags = new Set(clientFlags);
  (changes.remove || []).forEach((flag) => flags.delete(flag));
  (changes.add || []).forEach((flag) => flags.add(flag));
  return [...flags];
};

const splitLines = (value = '') =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const splitNegativeKeywords = (value = '') =>
  String(value || '')
    .split(/[,\r\n]+/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const splitTags = (value = '') =>
  String(value || '')
    .split(/[\s,]+/)
    .map((tag) => {
      const normalizedTag = tag.trim().replace(/^#+/, '');
      return normalizedTag ? `#${normalizedTag}` : '';
    })
    .filter(Boolean);

const getLegacyHour = (time, day, label) => {
  const [hour, minute] = String(time || '')
    .split(':')
    .map(Number);

  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute !== 0) {
    throw new Error(
      `${day} ${label} must use a whole hour to match the existing client format.`
    );
  }

  return hour;
};

const toBusinessHours = (hours = {}) =>
  DAYS.reduce((businessHours, day) => {
    const schedule = hours.days?.[day] || {
      isOpen: true,
      open24: false,
      openTime: '09:00',
      closeTime: '17:00'
    };

    if (!schedule || !schedule.isOpen) {
      businessHours[day] = '/';
    } else if (schedule.open24) {
      businessHours[day] = '0/24';
    } else {
      const open = getLegacyHour(schedule.openTime, day, 'opening time');
      const close = getLegacyHour(schedule.closeTime, day, 'closing time');
      businessHours[day] = `${open}/${close}`;
    }

    return businessHours;
  }, {});

const getContactPhone = (answers) =>
  answers.phoneShown === 'Second number' ? answers.phone2 : answers.phone;

const getAddress = (address = {}) =>
  [address?.street, address?.suite]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');

const getClientFlags = (answers) => {
  const platforms = answers.platforms ?? DEFAULT_SELECTED_PLATFORMS;
  const flags = DEFAULT_PLATFORMS.filter(
    (platform) => !platforms.includes(platform)
  ).map((platform) => PLATFORM_FLAGS[platform]);
  if (answers.images === 'Real photos only') flags.push('NO_AI');

  return flags;
};

export const buildClientPayload = (answers = {}) => {
  const businessType = answers.businessType || {};
  const website = answers.website || {};
  const hashtags = splitTags(answers.hashtags);
  const platforms = answers.platforms ?? DEFAULT_SELECTED_PLATFORMS;
  const selectedEstimates = (answers.estimates || []).filter(
    (option) => option !== 'Neither'
  );
  // "Neither" is persisted so /client can show it as the selected option
  const estimates =
    selectedEstimates.length === 0 &&
    (answers.estimates || []).includes('Neither')
      ? ['Neither']
      : selectedEstimates;
  const selectedHolidays = (answers.holidays || []).filter(
    (holiday) => holiday !== 'None'
  );

  return {
    clientName: String(answers.name || '')
      .trim()
      .replace(/\s+/g, ' '),
    timeZone: TIME_ZONE_VALUES[answers.timezone] || answers.timezone || '',
    serviceArea: splitLines(answers.areas),
    secondaryServiceArea: [],
    description: String(answers.special || ''),
    businessHours: toBusinessHours(answers.hours),
    mergeFields: {
      '~Address': getAddress(answers.address),
      '~Phone': String(getContactPhone(answers) || ''),
      '~Website': website.none ? '' : String(website.url || ''),
      '~Email': String(answers.email || '')
    },
    industry: getIndustryValue(
      businessType.choice || '',
      businessType.other || ''
    ),
    primaryService: splitLines(answers.services),
    priorityServices: splitLines(answers.priorityServices),
    estimates,
    specials: splitLines(answers.specials),
    ...(answers.businessType?.type === HOME_SERVICES_TYPE &&
      answers.businessType?.choice &&
      answers.gbpOffers && { gbpOffers: answers.gbpOffers }),
    bestTimes: { ...DEFAULT_BEST_TIMES },
    holidays: selectedHolidays,
    igTags: platforms.includes('Instagram') ? hashtags : [],
    fbTags: platforms.includes('Facebook') ? hashtags : [],
    negativeKeywords: splitNegativeKeywords(answers.avoid),
    galleries: {
      stockPhotos: { enabled: true },
      appLibrary: { enabled: false }
    },
    clientFlags: getClientFlags(answers),
    logoUrl: typeof answers.logo === 'string' ? answers.logo : '',
    brand: {
      primaryColor: '#3B5998',
      secondaryColor: '#8392A5'
    },
    tier: '',
    automaticReviews: true,
    teamSize: String(answers.teamSize || ''),
    singlePerson: answers.teamSize === 'Just me'
  };
};

export const buildOnboardingStepUpdate = (
  questionId,
  answers = {},
  currentClient = {},
  websiteAnswers = {}
) => {
  const payload = buildClientPayload(answers);
  const currentValue = answers[questionId];
  let update = {};

  const updateMergeField = (key, value) => {
    update = {
      mergeFields: {
        [key]: String(value || '')
      }
    };
  };

  switch (questionId) {
    case 'name':
      update = {
        clientName: payload.clientName,
        mergeFields: { '~Name': payload.clientName }
      };
      break;
    case 'phone':
      updateMergeField(
        '~Phone',
        answers.phoneShown === 'Second number' ? answers.phone2 : answers.phone
      );
      break;
    case 'phone2':
      update = {
        mergeFields: {
          '~SecondaryPhone': String(answers.phone2 || ''),
          ...(answers.phoneShown === 'Second number' && {
            '~Phone': String(answers.phone2 || '')
          })
        }
      };
      break;
    case 'phoneShown':
      updateMergeField(
        '~Phone',
        currentValue === 'Second number' ? answers.phone2 : answers.phone
      );
      break;
    case 'businessType':
      update = {
        industry: payload.industry,
        ...(answers.gbpOffers && { gbpOffers: answers.gbpOffers })
      };
      break;
    case 'address':
      updateMergeField('~Address', payload.mergeFields['~Address']);
      break;
    case 'timezone':
      update = { timeZone: payload.timeZone };
      break;
    case 'areas':
      update = { serviceArea: payload.serviceArea };
      break;
    case 'website':
      updateMergeField('~Website', payload.mergeFields['~Website']);
      break;
    case 'email':
      updateMergeField('~Email', payload.mergeFields['~Email']);
      break;
    case 'special':
      update = { description: payload.description };
      break;
    case 'licensed':
      if (currentValue) update = { licensed: currentValue };
      break;
    case 'yearStarted':
      if (currentValue !== undefined) update = { yearStarted: currentValue };
      break;
    case 'hours':
      update = {
        businessHours: payload.businessHours,
        emergencyService: currentValue?.emergency || ''
      };
      break;
    case 'services':
      update = { primaryService: payload.primaryService };
      break;
    case 'priorityServices':
      update = { priorityServices: payload.priorityServices };
      break;
    case 'specials':
      update = { specials: payload.specials };
      if (
        answers.businessType?.type === HOME_SERVICES_TYPE &&
        answers.businessType?.choice &&
        answers.gbpOffers
      ) {
        update.gbpOffers = answers.gbpOffers;
      }
      break;
    case 'gbpOffers':
      if (currentValue) update = { gbpOffers: currentValue };
      break;
    case 'estimates':
      update = { estimates: payload.estimates };
      break;
    case 'avoid':
      update = { negativeKeywords: payload.negativeKeywords };
      break;
    case 'holidays':
      update = { holidays: payload.holidays };
      break;
    case 'platforms': {
      const platforms =
        answers.platforms ?? getPlatformSelections(currentClient.clientFlags);
      const desiredFlags = DEFAULT_PLATFORMS.filter(
        (platform) => !platforms.includes(platform)
      ).map((platform) => PLATFORM_FLAGS[platform]);
      const changes = buildClientFlagChanges(
        currentClient.clientFlags || [],
        desiredFlags,
        Object.values(PLATFORM_FLAGS)
      );
      if (changes) {
        update = { clientFlagsUpdate: changes };
      }
      break;
    }
    case 'images': {
      const desiredFlags = currentValue === 'Real photos only' ? ['NO_AI'] : [];
      const changes = buildClientFlagChanges(
        currentClient.clientFlags || [],
        desiredFlags,
        ['NO_AI']
      );
      if (changes) {
        update = { clientFlagsUpdate: changes };
      }
      break;
    }
    case 'photos':
    case 'logo':
      break;
    case 'hashtags': {
      update = {
        igTags: payload.igTags,
        fbTags: payload.fbTags
      };
      break;
    }
    case 'teamSize':
      update = {
        teamSize: String(currentValue || ''),
        singlePerson: payload.singlePerson
      };
      break;
    case 'gbpManager': {
      const fields = currentValue || {};
      update = {
        gbpManager: fields.unsure
          ? { unsure: true }
          : {
              name: fields.vals?.[0] || '',
              email: fields.vals?.[1] || ''
            }
      };
      break;
    }
    case 'domain': {
      const fields = currentValue || {};
      update = {
        domainCompany: fields.unsure
          ? { unsure: true }
          : {
              provider: fields.vals?.[0] || '',
              username: fields.vals?.[1] || '',
              password: fields.vals?.[2] || ''
            }
      };
      break;
    }
    case 'socialLinks':
      update = { socialLinks: currentValue || {} };
      break;
    case 'goals':
      update = { goals: currentValue || [] };
      break;
    case 'competitors':
      update = { competitors: currentValue || {} };
      break;
    default:
      break;
  }

  if (questionId === 'websiteContent') {
    update = { websiteContent: websiteAnswers.content || '' };
  } else if (questionId === 'websiteColors') {
    update = { websiteColors: websiteAnswers.colors || '' };
  } else if (questionId === 'websiteLayout') {
    update = { websiteLayout: websiteAnswers.layout || '' };
  }

  return update;
};
